import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import type {
    MediaBatchContext,
    BuilderSvgInjectionParams,
    FileDescriptorImage,
    IdMapValue,
    UpdateFileDescriptorResponse,
    FileMediaType as FileMediaTypeType
} from '@wix/document-services-types'
import {FileMediaType} from '../../../constants/dsTypesConstants'
import {createMediaMap} from '../utils'
import {fetchSvgs} from './requests'
import {
    HandlerStatus,
    PayloadStatus,
    type EventHandlerExtApi,
    type Handler,
    type HandlerResult,
    type PayloadData
} from '../eventHandlerExtension'
import {handleAiError} from '../aiBuilderUtils'
import type {SchemaExtensionAPI} from '../../schema/schema'
import type {DataExtensionAPI} from '../../data'
import {DATA_TYPES} from '../../../constants/constants'
import {fromPairs, mapValues} from 'lodash'
import {uploadFilesWithHandlers, type UploadConfig} from '../fileUploadHandler'
import type {LoggerAPI, LoggerExtAPI} from '../../logger'
import {ReportableError} from '@wix/document-manager-utils'
import {ERRORS, LOGS} from '../constants'

const VECTOR_ART_DATA_TYPE = 'Builder.VectorArt'
const svgGenerationTimeout = 120000
const caller = 'generateSvgs'

interface SvgData {
    uri: string
}

const addLogsToContext = (context: MediaBatchContext<SvgData>, logger: LoggerAPI) => {
    const {onSuccess, onError} = context
    context.onSuccess = (arg: any) => {
        logger.interactionEnded(caller, {tags: {caller}})
        onSuccess?.(arg)
    }
    context.onError = (err: any) => {
        const reportableError = new ReportableError({
            errorType: ERRORS.SVGS.UPLOAD,
            message: 'Svg upload failed',
            extras: {
                originalError: err
            }
        })
        logger.captureError(reportableError, {tags: {caller}})
        onError?.(err)
    }
}

const injectSvg = (createExtArgs: CreateExtArgs, idMapValue: IdMapValue, svgData: SvgData | null) => {
    const {schemaAPI} = createExtArgs.extensionAPI as SchemaExtensionAPI
    const {data} = createExtArgs.extensionAPI as DataExtensionAPI
    const {pointer, prop, path, nestedDataPath} = idMapValue ?? {}

    if (!pointer) return

    let svgDataFormatted
    if (!svgData) {
        svgDataFormatted = null
    } else {
        svgDataFormatted = schemaAPI.createItemAccordingToSchema(VECTOR_ART_DATA_TYPE, DATA_TYPES.data, svgData)
    }

    const dataToUpdate = {[prop]: svgDataFormatted}

    data.injectData({pointer, path, nestedDataPath, partialNewData: dataToUpdate})
}

const createSvgUploadConfig = (logger: LoggerAPI): UploadConfig<SvgData | null> => ({
    extractData: (output: UpdateFileDescriptorResponse) => output?.file?.media?.vector?.image,
    transformData: (input: FileDescriptorImage): SvgData => ({uri: input.id!}),
    injectItem: injectSvg,
    errorMessage: 'Svg upload failed',
    timeOutInMS: 60000,
    onItemUploadStart: (url: string) => {
        logger.interactionStarted(LOGS.SVGS.UPLOAD, {tags: {caller}, extras: {url}})
    },
    onItemUploadComplete: (url: string) => {
        logger.interactionEnded(LOGS.SVGS.UPLOAD, {tags: {caller}, extras: {url}})
    }
})

const processSvgs = async (context: MediaBatchContext<SvgData>, svgMap: Record<string, string>) => {
    const {idMap, createExtArgs} = context

    Object.entries(svgMap).forEach(([mappedId, newSvgUrl]) => {
        injectSvg(createExtArgs, idMap[mappedId], {uri: newSvgUrl})
    })
    createExtArgs.dal.commitTransaction()

    const {logger} = context.createExtArgs.extensionAPI as LoggerExtAPI
    const svgUploadConfig = createSvgUploadConfig(logger)

    await uploadFilesWithHandlers(context, svgUploadConfig, svgMap)
}

const handleGeneralSvgError = (context: MediaBatchContext<SvgData>, errorMessage: string): HandlerResult => {
    context.onError?.({errors: [errorMessage]})
    return {status: HandlerStatus.FAILED, reason: errorMessage}
}

const createSvgHandler = (context: MediaBatchContext<SvgData>): Handler => {
    const {logger} = context.createExtArgs.extensionAPI as LoggerExtAPI

    return async (data: PayloadData): Promise<HandlerResult> => {
        const {status, output} = data ?? {}

        switch (status) {
            case PayloadStatus.FAILED:
                const error = new ReportableError({errorType: ERRORS.SVGS.GENERATION, message: 'SVG generation failed'})
                logger.captureError(error, {tags: {caller}})
                return handleGeneralSvgError(context, 'SVG generation failed')

            case PayloadStatus.COMPLETED:
                logger.interactionEnded(LOGS.SVGS.GENERATION, {tags: {caller}})
                addLogsToContext(context, logger)
                await processSvgs(context, output)
                return {status: HandlerStatus.COMPLETED}

            case PayloadStatus.RUNNING:
                return {status: HandlerStatus.RUNNING}
        }
    }
}

export async function applyBuilderSvgInjection(
    {serverFacade}: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    {
        compRefsWithHints,
        businessName,
        siteDescription,
        iconStyle,
        userRequestInfo,
        onSuccess,
        onError
    }: BuilderSvgInjectionParams
) {
    const {extensionAPI} = createExtArgs
    const {logger} = extensionAPI as LoggerExtAPI

    logger.interactionStarted(caller, {tags: {caller}})

    try {
        const compRefs = compRefsWithHints.map(({compRef}) => compRef)

        const {idMap, originalData} = createMediaMap(extensionAPI, compRefs, 'vectorArt', caller)

        const compIdToHint = fromPairs(compRefsWithHints.map(({compRef, hint}) => [compRef.id, hint]))
        const svgMappedIdsWithHints = mapValues(idMap, value => ({
            hint: compIdToHint[value.pointer.id]
        }))

        const context: MediaBatchContext<SvgData> = {
            createExtArgs,
            idMap,
            originalData,
            onSuccess,
            onError,
            totalItems: Object.keys(idMap).length,
            serverFacade,
            processedItems: 0,
            failures: [],
            mediaType: FileMediaType.VECTOR as FileMediaTypeType
        }

        logger.interactionStarted(LOGS.SVGS.GENERATION, {tags: {caller}})
        const executionId = await fetchSvgs(serverFacade, {
            svgs: svgMappedIdsWithHints,
            businessTerm: businessName,
            siteDescription,
            iconStyle,
            userRequestInfo
        })

        const handler = createSvgHandler(context)
        ;(extensionAPI as EventHandlerExtApi).eventHandler.registerExecutionIdToHandler(
            executionId,
            handler,
            caller,
            svgGenerationTimeout
        )
    } catch (e: any) {
        handleAiError(e, ERRORS.SVGS.GENERATION, logger, onError, {caller})
    }
}
